import React from 'react';
import tw from 'twin.macro';
import { CJAtelier } from 'types/cj';
import useDisclosureContext from 'components/Disclosure/useDisclosureContext';
import { AtelierTitle } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/AtelierTitle';
import Popover from 'components/Popover';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import { GroupEditor } from './GroupEditor';
import { Eleve } from 'types/eleves';
import { GroupTag } from 'containers/CahierJournal/components/GroupTag';
import { SupervisionTag } from 'containers/CahierJournal/components/SupervisionTag';
import { SupervisionEditor } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/SupervitionEditor';
import { EleveGroup } from 'types/eleveGroups';
import {
  handleGroupCreate,
  handleGroupChange,
  handleEphemeralGroupsChange,
} from './groupHandlers';

export function AtelierDisclosureButton({
  eleves,
  atelier,
  onChange,
  defaultOpen,
  groups,
  onCreateGroup,
  onDeleteGroup,
  onUpdateGroup,
  disableGroups,
}: {
  eleves: Eleve[];
  atelier: CJAtelier;
  onChange: (atelier: CJAtelier) => void;
  defaultOpen?: boolean;
  groups: EleveGroup[];
  onCreateGroup?: (g: EleveGroup) => Promise<any>;
  onDeleteGroup?: (id: string) => Promise<any>;
  onUpdateGroup?: (g: EleveGroup) => Promise<any>;
  disableGroups?: boolean;
}) {
  const { open } = useDisclosureContext();

  const atelierGroups = groups.filter(g =>
    (atelier?.groupPivots || []).map(gp => gp.id).includes(g.id),
  );

  return (
    <div tw="flex-grow">
      <div tw="flex items-center gap-x-2">
        <div>
          <Popover tw="flex" placement="bottom-start" portal>
            <Popover.Span>
              <div tw="space-x-2 flex items-center">
                <SupervisionTag
                  interactive
                  supervision={atelier?.supervision}
                />
              </div>
            </Popover.Span>
            <Popover.Panel size="2xl">
              <div
                onClick={e => e.stopPropagation()}
                tw="px-4 max-h-[20rem] overflow-y-auto"
              >
                <SupervisionEditor atelier={atelier} onChange={onChange} />
              </div>
            </Popover.Panel>
          </Popover>
        </div>
        <div>
          {!disableGroups && (
            <Popover tw="flex" placement="bottom-start" portal>
              <Popover.Span>
                <div
                  css={[
                    tw`gap-2 flex flex-wrap items-center rounded-lg border border-transparent max-w-[20rem]`,
                    atelierGroups.length > 0 &&
                      tw` hover:border hover:border-neutral-moderate hover:border-dashed`,
                  ]}
                >
                  {(atelierGroups || []).map(atelierGroup => (
                    <GroupTag
                      key={atelierGroup.id}
                      color={atelierGroup.color}
                      groupName={atelierGroup.name}
                      groupEleves={atelierGroup.eleves}
                      isEphemeral={atelierGroup.isEphemeral}
                      tw="truncate"
                    />
                  ))}

                  {atelierGroups.length === 0 && (
                    <GroupTag
                      tw="text-muted italic border border-dashed border-neutral-moderate hover:border-neutral-intense"
                      icon={UserPlusIcon}
                      data-testid="add-group-tag"
                      color="white"
                      interactive
                      groupName="Ajouter"
                    />
                  )}
                </div>
              </Popover.Span>
              <Popover.Panel size="2xl">
                <div
                  onClick={e => e.stopPropagation()}
                  tw="px-4 max-h-[20rem] overflow-y-auto"
                >
                  <GroupEditor
                    onDeleteGroup={onDeleteGroup}
                    eleves={eleves}
                    groups={groups}
                    groupIds={(atelier?.groupPivots || []).map(g => g.id)}
                    onChange={grps =>
                      handleGroupChange(atelier, grps, onChange)
                    }
                    onCreateGroup={g =>
                      handleGroupCreate(atelier, g, onChange, onCreateGroup)
                    }
                    onUpdateGroup={onUpdateGroup}
                    pendingEphemeralGroups={atelier.pendingEphemeralGroups}
                    onPendingEphemeralGroupsChange={pendingGroups =>
                      handleEphemeralGroupsChange(
                        atelier,
                        pendingGroups,
                        onChange,
                      )
                    }
                  />
                </div>
              </Popover.Panel>
            </Popover>
          )}
        </div>

        <div>
          <AtelierTitle
            title={atelier.title}
            onChange={v => onChange({ ...atelier, title: v })}
            isEditable={open}
            defaultOpen={defaultOpen}
          />
        </div>
      </div>
    </div>
  );
}
